import {
    Box,
    Button,
    Checkbox,
    Drawer,
    FormControlLabel,
    IconButton,
    Typography
} from '@mui/material'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useBranding } from '@/contexts/BrandingContext'
import useAuth from '@/hooks/useAuth'
import XIcon from '@/icons/X'
import ThemePreviewIcon from '@/theme/icons/ThemePreviewIcon'
import { resolveColorPreset } from '@/theme/utils'
import { OptionsColorPreset } from './options-color-preset'
import { OptionsContrast } from './options-contrast'
import { OptionsDirection } from './options-direction'
import { OptionsLayout } from './options-layout'
import { OptionsMapStyle } from './options-map-style'
import { OptionsNavColor } from './options-nav-color'
import { OptionsStretch } from './options-stretch'

const themes = [
    {
        label: 'White',
        value: 'white'
    },
    {
        label: 'Pastel',
        value: 'pastel'
    },
    {
        label: 'Grey',
        value: 'grey'
    },
    {
        label: 'Dark',
        value: 'dark'
    },
    {
        label: 'Black',
        value: 'black'
    },
    {
        label: 'Black X',
        value: 'black-experimental'
    }
]

const getValues = (settings) => ({
    colorPreset: settings.style.colorPreset,
    containerStretch: settings.style.containerStretch,
    direction: settings.style.direction,
    layout: settings.style.layout,
    responsiveFontSizes: settings.style.responsiveFontSizes,
    roundedCorners: settings.style.roundedCorners,
    theme: settings.style.theme,
    contrast: settings.style.contrast,
    navColor: settings.style.navColor
})

const SettingsDrawer = (props) => {
    const { open, onClose, ...other } = props
    const { settings, updateSettings } = useAuth()
    const { brand } = useBranding()
    const [values, setValues] = useState(getValues(settings))

    useEffect(() => {
        setValues(getValues(settings))
    }, [settings])

    // const handleSave = () => {
    //     updateSettings({
    //         'model'    : 'user',
    //         'attribute': ['style'],
    //         'value'    : values
    //     })
    //     // onClose?.()
    // }
    const handleChange = (field, value) => {
        const newValues = {
            ...values,
            [field]: value
        }

        setValues(newValues)

        updateSettings({
            model: 'user',
            attribute: ['style'],
            value: newValues
        })
    }

    return (
        <Drawer
            anchor="right"
            onClose={onClose}
            open={open}
            ModalProps={{
                BackdropProps: {
                    invisible: true
                },
                sx: { zIndex: 1400 }
            }}
            {...other}
            slotProps={{
                paper: { sx: { width: 320 } }
            }}
        >
            <Box
                sx={{
                    alignItems: 'center',
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2
                }}
            >
                <Typography color="inherit" variant="h6">
                    Display settings
                </Typography>
                <IconButton color="inherit" onClick={onClose}>
                    <XIcon fontSize="small" />
                </IconButton>
            </Box>
            <Box sx={{ py: 1, px: 3 }}>
                <Typography color="textSecondary" variant="overline">
                    Theme
                </Typography>
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-evenly',
                        flexWrap: 'wrap',
                        m: -1
                    }}
                >
                    {themes.map((theme) => {
                        const { label, value } = theme
                        return (
                            <div key={value}>
                                <Box
                                    onClick={() => handleChange('theme', value)}
                                    sx={[
                                        {
                                            borderRadius: settings.style
                                                .roundedCorners
                                                ? 1
                                                : 0,
                                            borderStyle: 'solid',
                                            borderWidth: 2,
                                            cursor: 'pointer',
                                            // flexBasis   : '33%',
                                            fontSize: 0,
                                            m: 1,
                                            overflow: 'hidden',
                                            p: 1,
                                            width: 75
                                        },
                                        values.theme === value
                                            ? {
                                                  borderColor: 'primary.main'
                                              }
                                            : {
                                                  borderColor: 'divider'
                                              }
                                    ]}
                                >
                                    <ThemePreviewIcon value={value} />
                                </Box>
                                <Typography
                                    align="center"
                                    sx={{ mt: 1 }}
                                    variant="subtitle2"
                                >
                                    {label}
                                </Typography>
                            </div>
                        )
                    })}
                </Box>

                <OptionsColorPreset
                    onChange={(value) => handleChange('colorPreset', value)}
                    // Shown resolved, saved as is: a user still on the site default
                    // keeps it when they change something else.
                    value={resolveColorPreset(
                        values.colorPreset,
                        brand.brand_key
                    )}
                />

                <OptionsMapStyle
                // onChange={(value) => handleChange('layout', value)}
                // value={values.layout}
                />

                <OptionsLayout
                    onChange={(value) => handleChange('layout', value)}
                    value={values.layout}
                />

                <OptionsStretch
                    onChange={(value) =>
                        handleChange('containerStretch', value)
                    }
                    // onChange={(value) => handleFieldUpdate('containerStretch', value)}
                    value={values.containerStretch}
                />

                <OptionsDirection
                    onChange={(value) => handleChange('direction', value)}
                    value={values.direction}
                />

                <OptionsNavColor
                    onChange={(value) => handleChange('navColor', value)}
                    value={values.navColor}
                />
                <OptionsContrast
                    onChange={(value) => handleChange('contrast', value)}
                    value={values.contrast}
                />

                <Typography
                    color="textSecondary"
                    sx={{ display: 'block', mb: 1, mt: 4 }}
                    variant="overline"
                >
                    Settings
                </Typography>

                <div>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={values.roundedCorners === true}
                                name="roundedCorners"
                                onChange={(event) =>
                                    handleChange(
                                        'roundedCorners',
                                        event.target.checked
                                    )
                                }
                            />
                        }
                        label={
                            <Typography variant="subtitle2">
                                Rounded Corners
                            </Typography>
                        }
                    />
                </div>

                <div>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={values.responsiveFontSizes}
                                name="direction"
                                onChange={(event) =>
                                    handleChange(
                                        'responsiveFontSizes',
                                        event.target.checked
                                    )
                                }
                            />
                        }
                        label={
                            <Typography variant="subtitle2">
                                Responsive font sizes
                            </Typography>
                        }
                    />
                </div>
            </Box>
        </Drawer>
    )
}

SettingsDrawer.propTypes = {
    onClose: PropTypes.func,
    open: PropTypes.bool
}

export default SettingsDrawer
